import { SECTION_SLUGS } from '@massar/shared/constants';
import { P } from '@massar/shared/permissions';

/**
 * The guided tours, defined once.
 *
 * ### Why this exists
 *
 * Massar is four businesses in one application. A person who has only ever worked in Real
 * Estate opens it and sees Budgets, Deals, BOQ, Retention and a Main Safe, and nothing on
 * the screen says which of those are *theirs*. The sidebar answers "what may I click", not
 * "what is this for", and those are different questions — the second one is usually asked
 * of a colleague, once, and then never revisited.
 *
 * ### The shape
 *
 * A tour is a list of steps. A step is either
 *   - **narrative** — a card in the middle of the screen; used for the welcome tour, which
 *     explains the four business lines before the user has any screen to point at, and
 *   - **anchored** — `target` is a CSS selector; the step spotlights that element and
 *     positions itself beside it.
 *
 * Steps carry `permission` or `section` where they describe something not everybody has.
 * A tour that shows a broker the period-lock button is worse than no tour: it teaches a
 * screen they will never see and hides the two that matter to them. Filtering happens at
 * render, against the same `can()` the navigation uses, so a step and the menu item it
 * points at can never disagree.
 *
 * `title` and `body` are i18n **keys**, not strings — the tour ships in all three
 * languages like everything else, and the parity test covers them.
 */

/** Bump to re-show a tour that has genuinely changed. Completions below this re-open. */
export const ONBOARDING_VERSION = 1;

export const TOUR = {
  WELCOME: 'welcome',
  DASHBOARD: 'dashboard',
  TREASURY: 'treasury',
  CURRENCIES: 'currencies',
  DOCUMENTS: 'documents',
};

/**
 * The first-login tour: what this system is, and which section does what.
 *
 * Narrative rather than anchored, deliberately. It runs before the user has looked at
 * anything, and spotlighting a sidebar item to explain "Real Estate sells and rents
 * property" would teach them where a link is when what they need is what the business
 * does. The section cards are rendered from `SECTION_SLUGS`, so a step cannot describe a
 * business line the system does not have.
 */
export const WELCOME_STEPS = [
  { id: 'intro', title: 'welcome.intro.title', body: 'welcome.intro.body' },
  {
    id: 'sections',
    title: 'welcome.sections.title',
    body: 'welcome.sections.body',
    /** Renders the four business lines as cards — see `SectionsStep`. */
    render: 'sections',
  },
  { id: 'safes', title: 'welcome.safes.title', body: 'welcome.safes.body', render: 'safes' },
  { id: 'money', title: 'welcome.money.title', body: 'welcome.money.body' },
  { id: 'ledger', title: 'welcome.ledger.title', body: 'welcome.ledger.body' },
  { id: 'finding', title: 'welcome.finding.title', body: 'welcome.finding.body' },
];

/** What each business line is actually for — the answer to "which section do I use?". */
export const SECTION_GUIDE = [
  { slug: SECTION_SLUGS.REAL_ESTATE, key: 'realestate' },
  { slug: SECTION_SLUGS.AGENCY, key: 'agency' },
  { slug: SECTION_SLUGS.BROKERS, key: 'brokers' },
  { slug: SECTION_SLUGS.CONSTRUCTION, key: 'construction' },
  { slug: SECTION_SLUGS.CLEANER, key: 'cleaner' },
];

/**
 * Per-screen tours, keyed by the route they belong to.
 *
 * `target` selectors point at `data-tour="…"` attributes rather than class names or
 * structural selectors. A class is a styling decision and will be changed by somebody
 * refactoring Tailwind; a `data-tour` attribute exists only for this and survives.
 */
export const SCREEN_TOURS = {
  '/': {
    id: TOUR.DASHBOARD,
    steps: [
      {
        id: 'shell',
        target: '[data-tour="sidebar"]',
        title: 'dashboard.shell.title',
        body: 'dashboard.shell.body',
      },
      {
        id: 'section-switcher',
        target: '[data-tour="section-switcher"]',
        title: 'dashboard.switcher.title',
        body: 'dashboard.switcher.body',
      },
      {
        id: 'period',
        target: '[data-tour="period-badge"]',
        title: 'dashboard.period.title',
        body: 'dashboard.period.body',
      },
      {
        id: 'search',
        target: '[data-tour="global-search"]',
        title: 'dashboard.search.title',
        body: 'dashboard.search.body',
      },
      {
        id: 'help',
        target: '[data-tour="help"]',
        title: 'dashboard.help.title',
        body: 'dashboard.help.body',
      },
    ],
  },

  '/treasury/safes': {
    id: TOUR.TREASURY,
    steps: [
      { id: 'what', title: 'treasury.what.title', body: 'treasury.what.body' },
      {
        id: 'compartments',
        title: 'treasury.compartments.title',
        body: 'treasury.compartments.body',
      },
      {
        id: 'main',
        title: 'treasury.main.title',
        body: 'treasury.main.body',
        permission: P.REPORT_CONSOLIDATED_VIEW,
        global: true,
      },
      {
        id: 'adjust',
        title: 'treasury.adjust.title',
        body: 'treasury.adjust.body',
        permission: P.SAFE_ADJUST,
      },
    ],
  },

  '/settings/currencies': {
    id: TOUR.CURRENCIES,
    steps: [
      { id: 'what', title: 'currencies.what.title', body: 'currencies.what.body' },
      { id: 'base', title: 'currencies.base.title', body: 'currencies.base.body' },
      {
        id: 'rates',
        title: 'currencies.rates.title',
        body: 'currencies.rates.body',
        permission: P.CURRENCY_MANAGE,
        global: true,
      },
    ],
  },

  '/invoices': {
    id: TOUR.DOCUMENTS,
    steps: [
      { id: 'lifecycle', title: 'documents.lifecycle.title', body: 'documents.lifecycle.body' },
      { id: 'immutable', title: 'documents.immutable.title', body: 'documents.immutable.body' },
      { id: 'currency', title: 'documents.currency.title', body: 'documents.currency.body' },
    ],
  },
};

/** The tour that belongs to a path, if any. Exact match — a tour is about one screen. */
export const tourForPath = (pathname) => SCREEN_TOURS[pathname] ?? null;

/** Every tour a user could be offered, for the "replay" menu. */
export const ALL_TOURS = [
  { id: TOUR.WELCOME, path: null },
  ...Object.entries(SCREEN_TOURS).map(([path, tour]) => ({ id: tour.id, path })),
];
